/**
 * Confirmación del impacto en Visma, por colaborador.
 *
 * Es la única escritura del módulo y no tiene deshacer desde el portal: el
 * modal dice qué se va a mandar, deja sacar lo que no corresponda y cuenta lo
 * que queda afuera.
 *
 * La lista va por persona —"a Pérez le toca esto"—, que es como se revisa una
 * liquidación, y cada fila se abre para ver sus conceptos. La casilla y el
 * resto de la fila son dos blancos distintos: una selecciona y el otro
 * despliega.
 *
 * Lo que se manda son **claves**, no importes: el servidor rehace la consulta
 * al TMS y recalcula cada novedad. Si el front mandara el valor, cualquiera con
 * la consola abierta podría liquidar lo que quisiera.
 */

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search, Send, X } from 'lucide-react';

import type { NovedadConsolidada, NovedadRechazada, ResumenConcepto } from '../../api';
import { Aviso } from '../../components/Aviso';
import { Modal } from '../../components/Modal';
import { mesYAnio, soloFecha } from '../../lib/fechas';
import {
  agruparPorColaborador,
  alternarGrupo,
  alternarUna,
  coincide,
  conceptoDeClave,
  esDniUtilizable,
  estadoDe,
  rangoLegible,
  type EstadoCasilla,
} from './seleccion';

/**
 * Cómo terminó un intento de impacto.
 *
 * El tono distingue tres cosas distintas: entró todo, entró una parte, o no
 * entró nada. Pintar de rojo una carga que entró a medias hace pensar que no
 * entró nada.
 */
export interface ResultadoIntento {
  readonly tono: 'ok' | 'atencion' | 'error';
  readonly texto: string;
  readonly rechazadas: readonly NovedadRechazada[];
}

/** Una novedad que el portal no puede mandar, con el motivo. */
export interface Trabada {
  readonly dni: string;
  readonly nombre: string;
  readonly concepto: string;
  readonly valor: number;
  readonly motivo: string;
}

export interface PropsDialogoImpactar {
  readonly abierto: boolean;
  readonly operacion: string;
  readonly periodo: string;
  /** Solo las novedades de los conceptos tildados en la pantalla. */
  readonly novedades: readonly NovedadConsolidada[];
  readonly conceptos: readonly ResumenConcepto[];
  /**
   * Motivo por el que no se puede impactar nada: un concepto sin parámetro, la
   * sesión vencida. Con motivo, el botón se apaga y el modal igual deja
   * revisar lo que se mandaría.
   *
   * No es lo mismo que el resultado de un intento: eso va en `intento`, no
   * apaga el botón y deja seguir eligiendo.
   */
  readonly bloqueo: string | null;
  /** Lo que el servidor no va a mandar aunque esté tildado, con el motivo. */
  readonly trabadas: readonly Trabada[];
  /**
   * Cómo terminó el último intento. Se muestra **adentro** del modal y no lo
   * cierra: después de cargar una parte, lo normal es seguir con el resto.
   */
  readonly intento: ResultadoIntento | null;
  readonly preparando: boolean;
  readonly enviando: boolean;
  /**
   * Si el portal permite escribir a mano el DNI con el que cada colaborador
   * entra a Visma. Lo decide el servidor, que lo vuelve a exigir: esto solo
   * decide si se dibuja el campo.
   */
  readonly dniEditable: boolean;
  readonly onCerrar: () => void;
  readonly onImpactar: (
    claves: ReadonlySet<string>,
    dniManual: ReadonlyMap<string, string>,
  ) => void;
}

const numero = (valor: number): string =>
  valor.toLocaleString('es-AR', { maximumFractionDigits: 2 });

export function DialogoImpactar(props: PropsDialogoImpactar): React.JSX.Element {
  const {
    abierto,
    operacion,
    periodo,
    novedades,
    conceptos,
    bloqueo,
    trabadas,
    intento,
    preparando,
    enviando,
    dniEditable,
    onCerrar,
    onImpactar,
  } = props;

  const grupos = useMemo(
    () => agruparPorColaborador(novedades, conceptos),
    [novedades, conceptos],
  );
  const todas = useMemo(() => grupos.flatMap((g) => g.lineas.map((l) => l.clave)), [grupos]);

  const [elegidas, setElegidas] = useState<ReadonlySet<string>>(() => new Set(todas));
  const [abiertos, setAbiertos] = useState<ReadonlySet<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  /** DNI escritos a mano, por colaborador. Vacío fuera de la etapa de pruebas. */
  const [dniManual, setDniManual] = useState<ReadonlyMap<string, string>>(new Map());

  /*
   * Cada apertura arranca de cero: todo tildado, todo cerrado y sin buscar.
   * Conservar la selección anterior deja a quien cerró a la mitad frente a un
   * modal que no dice que le falta algo.
   */
  const [abiertoAntes, setAbiertoAntes] = useState(false);
  if (abierto !== abiertoAntes) {
    setAbiertoAntes(abierto);
    if (abierto) {
      setElegidas(new Set(todas));
      setAbiertos(new Set());
      setBusqueda('');
      setDniManual(new Map());
    }
  }

  /*
   * Y cuando la lista cambia con el modal abierto —porque se impactó una
   * parte y el servidor volvió a decir qué queda— se tilda de nuevo lo que
   * queda. Conservar las claves viejas dejaría contadores que no cierran y
   * un botón que dice un número que ya no existe.
   */
  const firma = todas.join(',');
  const [firmaAnterior, setFirmaAnterior] = useState(firma);
  if (abierto && firma !== firmaAnterior) {
    setFirmaAnterior(firma);
    setElegidas(new Set(todas));
  }

  /**
   * El documento con el que una persona va a entrar a Visma.
   *
   * Si hay una entrada en el mapa manda esa, **aunque esté vacía**: borrar el
   * campo para escribir otro DNI es un estado legítimo mientras se tipea, y
   * reponer el original en ese momento hace imposible reemplazarlo.
   */
  const dniDe = (dni: string): string => dniManual.get(dni) ?? dni;

  const cambiarDni = (dni: string, valor: string): void => {
    setDniManual((actuales) => {
      const proximos = new Map(actuales);
      // Volver a escribir el original es borrar la excepción: así el back
      // recibe solo lo que de verdad se cambió. El campo vacío, en cambio, se
      // guarda como vacío, y la validación de abajo frena el envío.
      if (valor.trim() === dni) proximos.delete(dni);
      else proximos.set(dni, valor.trim());
      return proximos;
    });
  };

  /*
   * Un DNI escrito a mano que no es un documento frena el envío entero: el
   * back lo rechazaría igual, y es mejor decirlo antes de mandar que después.
   */
  const dniRotos = grupos.filter(
    (g) => g.lineas.some((l) => elegidas.has(l.clave)) && !esDniUtilizable(dniDe(g.dni)),
  );

  const visibles = grupos.filter((g) => coincide(g, busqueda));
  const estadoTodo = estadoDe(todas, elegidas);
  const colaboradores = grupos.filter((g) =>
    g.lineas.some((l) => elegidas.has(l.clave)),
  ).length;
  const conceptosDistintos = new Set([...elegidas].map(conceptoDeClave)).size;
  const afuera = todas.length - elegidas.size;
  const todosAbiertos = visibles.length > 0 && visibles.every((g) => abiertos.has(g.dni));

  const puede =
    bloqueo === null &&
    elegidas.size > 0 &&
    dniRotos.length === 0 &&
    !enviando &&
    !preparando;

  return (
    <Modal
      abierto={abierto}
      titulo="Impactar en Visma"
      identidad={`${operacion.toUpperCase()} · liquidación ${mesYAnio(periodo)}`}
      ancho="amplio"
      onCerrar={enviando ? () => undefined : onCerrar}
      pie={
        <>
          <span className={`impactar-pie-nota${elegidas.size === 0 ? ' es-error' : ''}`}>
            {elegidas.size === 0
              ? 'No hay nada seleccionado para impactar.'
              : afuera > 0
                ? `${String(afuera)} novedad(es) quedan afuera. No se puede deshacer desde el portal.`
                : 'No se puede deshacer desde el portal.'}
          </span>
          <button type="button" className="plano" disabled={enviando} onClick={onCerrar}>
            <X size={15} aria-hidden="true" />
            Cancelar
          </button>
          <button
            type="button"
            className="primaria"
            disabled={!puede}
            aria-busy={enviando}
            onClick={() => onImpactar(elegidas, dniManual)}
          >
            <Send size={16} aria-hidden="true" />
            {enviando
              ? 'Impactando…'
              : elegidas.size > 0
                ? `Impactar ${String(elegidas.size)} novedad(es)`
                : 'Impactar en Visma'}
          </button>
        </>
      }
    >
      <div className="resumen">
        <Dato
          etiqueta="Colaboradores"
          valor={`${String(colaboradores)} de ${String(grupos.length)}`}
        />
        <Dato etiqueta="Novedades a impactar" valor={numero(elegidas.size)} />
        <Dato etiqueta="Conceptos distintos" valor={String(conceptosDistintos)} />
      </div>

      {preparando && (
        <Aviso tono="info">
          Se está verificando contra el TMS y Visma qué se puede impactar…
        </Aviso>
      )}
      {bloqueo !== null && <Aviso tono="error">{bloqueo}</Aviso>}

      {intento !== null && <Aviso tono={intento.tono}>{intento.texto}</Aviso>}

      {intento !== null && intento.rechazadas.length > 0 && (
        <Aviso tono="atencion">
          {`Visma rechazó ${String(intento.rechazadas.length)} novedad(es): ` +
            `${intento.rechazadas
              .slice(0, 3)
              .map((r) => `${r.dni}/${r.concepto} (${r.motivo})`)
              .join(' · ')}${intento.rechazadas.length > 3 ? ' …' : ''}. Siguen en la lista: ` +
            'corregido el motivo, se pueden volver a mandar.'}
        </Aviso>
      )}

      {trabadas.length > 0 && (
        <Aviso tono="atencion">
          {`${String(trabadas.length)} novedad(es) no se pueden mandar aunque estén tildadas: ` +
            `${trabadas
              .slice(0, 3)
              .map((t) => `${t.nombre || t.dni} — ${t.motivo}`)
              .join(' · ')}${trabadas.length > 3 ? ' …' : ''}`}
        </Aviso>
      )}

      {dniEditable && (
        <Aviso tono="atencion">
          Etapa de pruebas: el DNI con el que cada colaborador entra a Visma se puede escribir a
          mano, porque los de la base de pruebas están ofuscados y no existen en Visma. Lo que se
          edite acá es el documento al que se le liquida.
        </Aviso>
      )}

      {dniRotos.length > 0 && (
        <Aviso tono="error">
          {`${String(dniRotos.length)} colaborador(es) tienen un DNI que no es un documento ` +
            `válido: ${dniRotos
              .slice(0, 3)
              .map((g) => g.nombre)
              .join(', ')}${dniRotos.length > 3 ? '…' : ''}.`}
        </Aviso>
      )}

      <div className="impactar-herramientas">
        <label className="campo-busqueda">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            placeholder="Buscar DNI, nombre o legajo"
            aria-label="Buscar colaborador"
            value={busqueda}
            onChange={(ev) => setBusqueda(ev.target.value)}
          />
        </label>
        <button
          type="button"
          className="plano"
          onClick={() =>
            setAbiertos(todosAbiertos ? new Set() : new Set(visibles.map((g) => g.dni)))
          }
        >
          {todosAbiertos ? 'Cerrar todos' : 'Abrir todos'}
        </button>
      </div>

      <div className="impactar-lista">
        <div
          className="impactar-fila impactar-fila--encabezado"
          data-editable={dniEditable ? 'si' : 'no'}
        >
          <Casilla
            estado={estadoTodo}
            etiqueta="Seleccionar todas las novedades"
            onCambiar={() => setElegidas(alternarGrupo(todas, elegidas))}
          />
          <span />
          <button
            type="button"
            className="enlace"
            onClick={() => setElegidas(alternarGrupo(todas, elegidas))}
          >
            {estadoTodo === 'si' ? 'Quitar todo' : 'Seleccionar todo'}
          </button>
          {/* El orden sigue al de la fila: con el DNI editable, el campo va
              después del contador porque sale del botón que despliega. */}
          {dniEditable ? (
            <>
              <span className="num">A impactar</span>
              <span className="num">DNI a impactar</span>
            </>
          ) : (
            <>
              <span className="num">DNI</span>
              <span className="num">A impactar</span>
            </>
          )}
        </div>

        <div className="impactar-cuerpo">
          {visibles.map((g) => {
            const claves = g.lineas.map((l) => l.clave);
            const estado = estadoDe(claves, elegidas);
            const cuantas = claves.filter((c) => elegidas.has(c)).length;
            const abiertoG = abiertos.has(g.dni);

            return (
              <div key={g.dni} className="impactar-grupo" data-abierto={abiertoG ? 'si' : 'no'}>
                <div
                  className="impactar-fila"
                  data-vacio={cuantas === 0 ? 'si' : 'no'}
                  data-editable={dniEditable ? 'si' : 'no'}
                >
                  <Casilla
                    estado={estado}
                    etiqueta={`Impactar las novedades de ${g.nombre}`}
                    onCambiar={() => setElegidas(alternarGrupo(claves, elegidas))}
                  />
                  <button
                    type="button"
                    className="impactar-desplegar"
                    aria-expanded={abiertoG}
                    onClick={() =>
                      setAbiertos((actuales) => {
                        const proximos = new Set(actuales);
                        if (!proximos.delete(g.dni)) proximos.add(g.dni);
                        return proximos;
                      })
                    }
                  >
                    <span className="chevron" aria-hidden="true">
                      {abiertoG ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    <span className="persona">
                      <strong>{g.nombre}</strong>
                      <span className="sub">
                        {`leg. ${g.legajo}`}
                        {/* Se marca al eventual y no al propio: lo que hay que
                            ver de un vistazo es quién no es de la casa. */}
                        {g.tipoChofer === 'Eventual' && (
                          <span
                            className="chip chip--eventual"
                            title="Chofer de una empresa ajena al grupo"
                          >
                            Eventual
                          </span>
                        )}
                      </span>
                    </span>
                    {/* Con el DNI editable, el campo va afuera del botón: un
                        input adentro no se puede usar sin desplegar la fila. */}
                    {!dniEditable && <span className="num tenue">{g.dni}</span>}
                    <span className="num">
                      <strong>{cuantas}</strong>
                      <span className="tenue">{` de ${String(claves.length)}`}</span>
                    </span>
                  </button>

                  {dniEditable && (
                    <input
                      type="text"
                      inputMode="numeric"
                      className="dni-visma"
                      data-cambiado={dniManual.has(g.dni) ? 'si' : 'no'}
                      data-roto={esDniUtilizable(dniDe(g.dni)) ? 'no' : 'si'}
                      aria-label={`DNI con el que se impacta a ${g.nombre}`}
                      title={`DNI del TMS: ${g.dni}`}
                      value={dniDe(g.dni)}
                      onChange={(ev) => cambiarDni(g.dni, ev.target.value)}
                    />
                  )}
                </div>

                {abiertoG && (
                  <table className="tabla impactar-detalle">
                    <thead>
                      <tr>
                        <th scope="col" className="col-check">
                          <span className="sr-only">Impactar</span>
                        </th>
                        <th scope="col" className="num">Código</th>
                        <th scope="col">Descripción</th>
                        {/* Son las fechas del HECHO, no el período: período es
                            mes y año, y llamarlas así confunde las dos cosas. */}
                        <th scope="col" className="num">Fechas</th>
                        <th scope="col" className="num">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.lineas.map((l) => {
                        const elegida = elegidas.has(l.clave);
                        return (
                          <tr
                            key={l.clave}
                            data-elegido={elegida ? 'si' : 'no'}
                            onClick={() => setElegidas(alternarUna(l.clave, elegidas))}
                          >
                            <td className="col-check">
                              <input
                                type="checkbox"
                                checked={elegida}
                                aria-label={`Impactar ${l.concepto} de ${g.nombre}`}
                                // La fila entera alterna: sin esto el clic en
                                // la casilla llega dos veces y se anula.
                                onClick={(ev) => ev.stopPropagation()}
                                onChange={() => setElegidas(alternarUna(l.clave, elegidas))}
                              />
                            </td>
                            <td className="num codigo">{l.concepto}</td>
                            <td>{l.descripcion || '—'}</td>
                            <td className="num tenue">
                              {rangoLegible(soloFecha(l.fechaDesde), soloFecha(l.fechaHasta))}
                            </td>
                            <td className="num">
                              <strong>{numero(l.valor)}</strong>
                              {l.unidad !== '' && <span className="unidad">{l.unidad}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          {visibles.length === 0 && (
            <p className="tabla-vacia">
              {grupos.length === 0
                ? 'No queda ninguna novedad por cargar en este período.'
                : `Ningún colaborador coincide con "${busqueda}".`}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Casilla de tres estados.
 *
 * Es un `<input type="checkbox">` de verdad con `indeterminate`, que no existe
 * como atributo HTML y se pone por ref. Así se navega con teclado, el lector de
 * pantalla anuncia "parcialmente marcada" y toma `accent-color` sin ARIA a
 * mano.
 */
function Casilla({
  estado,
  etiqueta,
  onCambiar,
}: {
  readonly estado: EstadoCasilla;
  readonly etiqueta: string;
  readonly onCambiar: () => void;
}): React.JSX.Element {
  return (
    <input
      type="checkbox"
      className="casilla"
      aria-label={etiqueta}
      checked={estado === 'si'}
      ref={(el) => {
        if (el) el.indeterminate = estado === 'parcial';
      }}
      onChange={onCambiar}
    />
  );
}

function Dato({
  etiqueta,
  valor,
}: {
  readonly etiqueta: string;
  readonly valor: string;
}): React.JSX.Element {
  return (
    <div className="dato">
      <span className="etiqueta">{etiqueta}</span>
      <strong>{valor}</strong>
    </div>
  );
}
